/**
 * Период финансового блока.
 *
 * Календарный: месяц, квартал или год. Скользящие «последние 30 дней»
 * сюда не годятся — витрины помесячные, а клиент мыслит закрытыми месяцами.
 *
 * Прошлый период считается здесь же, календарно, и запрашивается отдельным
 * запросом: серверный `compare: previous_period` сдвигает период на столько же
 * дней назад, и для 31-дневного месяца после 30-дневного захватывает два
 * месяца (июль сравнивается с маем и июнем вместе).
 */

export type Grain = 'month' | 'quarter' | 'year'

export interface PeriodSelection {
  grain: Grain
  /** Первое число периода, YYYY-MM-DD. */
  start: string
}

export interface DateRange {
  from: string
  to: string
}

const MONTHS = [
  'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
]

const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']

const QUARTERS = ['I', 'II', 'III', 'IV']

const iso = (year: number, monthIndex: number, day = 1): string =>
  new Date(Date.UTC(year, monthIndex, day)).toISOString().slice(0, 10)

const parts = (date: string) => ({ year: Number(date.slice(0, 4)), month: Number(date.slice(5, 7)) - 1 })

export function todayIso(): string {
  const now = new Date()
  return iso(now.getFullYear(), now.getMonth(), now.getDate())
}

/** По умолчанию — последний закончившийся месяц: текущий ещё не закрыт в 1С. */
export function defaultSelection(): PeriodSelection {
  const now = new Date()
  return { grain: 'month', start: iso(now.getFullYear(), now.getMonth() - 1) }
}

export function normalize(selection: PeriodSelection): PeriodSelection {
  const { year, month } = parts(selection.start)
  if (selection.grain === 'year') return { grain: 'year', start: iso(year, 0) }
  if (selection.grain === 'quarter') return { grain: 'quarter', start: iso(year, Math.floor(month / 3) * 3) }
  return { grain: 'month', start: iso(year, month) }
}

const SPAN: Record<Grain, number> = { month: 1, quarter: 3, year: 12 }

export function rangeOf(selection: PeriodSelection): DateRange {
  const { year, month } = parts(selection.start)
  return { from: selection.start, to: iso(year, month + SPAN[selection.grain], 0) }
}

export function shift(selection: PeriodSelection, steps: number): PeriodSelection {
  const { year, month } = parts(selection.start)
  return { grain: selection.grain, start: iso(year, month + steps * SPAN[selection.grain]) }
}

export const previousOf = (selection: PeriodSelection): PeriodSelection => shift(selection, -1)

/** Период ещё идёт: сравнивать его с законченным прошлым нечестно. */
export function isIncomplete(selection: PeriodSelection): boolean {
  return rangeOf(selection).to >= todayIso()
}

export function isFuture(selection: PeriodSelection): boolean {
  return selection.start > todayIso()
}

export function labelOf(selection: PeriodSelection): string {
  const { year, month } = parts(selection.start)
  if (selection.grain === 'year') return `${year} год`
  if (selection.grain === 'quarter') return `${QUARTERS[Math.floor(month / 3)]} квартал ${year}`
  const name = MONTHS[month] ?? ''
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${year}`
}

/** «к августу», «ко II кварталу», «к 2025 году» — подпись к изменению. */
export function compareLabel(selection: PeriodSelection): string {
  const previous = previousOf(selection)
  const { year, month } = parts(previous.start)
  if (selection.grain === 'year') return `к ${year} году`
  if (selection.grain === 'quarter') {
    return `к ${QUARTERS[Math.floor(month / 3)] ?? ''} кв. ${year}`
  }
  const dative = [
    'январю', 'февралю', 'марту', 'апрелю', 'маю', 'июню',
    'июлю', 'августу', 'сентябрю', 'октябрю', 'ноябрю', 'декабрю',
  ]
  return `к ${dative[month]}`
}

/** Двенадцать месяцев, которыми заканчивается выбранный период. */
export function trendRange(selection: PeriodSelection): DateRange {
  const range = rangeOf(selection)
  const { year, month } = parts(range.to)
  return { from: iso(year, month - 11), to: range.to }
}

export function monthsOf(range: DateRange): string[] {
  const result: string[] = []
  const { year, month } = parts(range.from)
  for (let index = 0; ; index += 1) {
    const value = iso(year, month + index)
    if (value > range.to) break
    result.push(value)
    if (index > 60) break
  }
  return result
}

export function shortMonth(date: string): string {
  return MONTHS_SHORT[parts(date).month] ?? date
}

export function monthLabel(date: string): string {
  const { year, month } = parts(date)
  return `${MONTHS[month] ?? ''} ${year}`
}

// --- Адрес -------------------------------------------------------------------

export function parseSelection(value: string | null): PeriodSelection {
  if (!value) return defaultSelection()
  const [grain, date] = value.split(':')
  if (grain === 'year' && /^\d{4}$/.test(date ?? '')) return { grain, start: `${date}-01-01` }
  if ((grain === 'month' || grain === 'quarter') && /^\d{4}-\d{2}$/.test(date ?? '')) {
    return normalize({ grain, start: `${date}-01` })
  }
  return defaultSelection()
}

export function formatSelection(selection: PeriodSelection): string {
  if (selection.grain === 'year') return `year:${selection.start.slice(0, 4)}`
  return `${selection.grain}:${selection.start.slice(0, 7)}`
}
